import React, { type ReactNode } from 'react';

import { Button } from '../Button';
import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

/**
 * EAA-1821: the "count · prev · page · next" footer under a hand-built table.
 *
 * Every copy of this in the console rendered its disabled prev/next as a dimmed
 * button that was still clickable — which fires a request for page 0 and then
 * renders an empty table. Here `disabled` is BOTH: the real attribute (so it
 * cannot fire) and the visual treatment (so it cannot look clickable).
 *
 * All copy arrives as nodes, already formatted by the caller: this package
 * holds no domain vocabulary and cannot know whether a page counter should read
 * "Trang 2/9" or "2 of 9".
 */

export interface TablePagerLabels {
    prev: ReactNode;
    next: ReactNode;
    /** Already-formatted page counter, e.g. "Trang 2/9". */
    page: ReactNode;
}

export interface TablePagerProps extends Styleable {
    page: number;
    totalPages: number;
    onPrev: () => void;
    onNext: () => void;
    labels: TablePagerLabels;
    /** Leading readout, e.g. "Hiển thị 20 / 218 bản ghi". */
    summary?: ReactNode;
}

export function TablePager({
    page,
    totalPages,
    onPrev,
    onNext,
    labels,
    summary,
    className,
    style,
}: TablePagerProps): React.ReactElement {
    const colors = useUIColors();

    const buttonStyle: React.CSSProperties = {
        height: 32,
        padding: '0 12px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 500,
    };

    return (
        <div
            className={cx('ui-table-pager', className)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                color: colors.textSecondary,
                fontSize: 13,
                ...style,
            }}
        >
            {summary && <span className="ui-table-pager__summary">{summary}</span>}
            <span className="ui-table-pager__spacer" style={{ flex: '1 1 auto' }} />
            <Button
                className="ui-table-pager__prev"
                disabled={page <= 1}
                onClick={onPrev}
                style={buttonStyle}
            >
                {labels.prev}
            </Button>
            <span
                className="ui-table-pager__page"
                style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
            >
                {labels.page}
            </span>
            <Button
                className="ui-table-pager__next"
                disabled={page >= totalPages}
                onClick={onNext}
                style={buttonStyle}
            >
                {labels.next}
            </Button>
        </div>
    );
}

export default TablePager;
